// RangeChips — the reporting-window selector for the focused report views
// (delivery, KPI compliance, financial performance). Wears the shared report
// chip register from reportChipStyles, so it cannot drift from the
// record-type chips that sit on the same line.
//
// Deliberately NOT shown on the Summary view. The summary is a
// contract-to-date position statement composed of nine different domains,
// several of which have no meaningful window at all, so a range control there
// would imply a filter it does not apply.
//
// The row is a plain multi-button group (aria-pressed) rather than a
// radiogroup: it sits beside content rather than inside a form, and it
// matches the sub-view toggle treatment used elsewhere in the contract
// workspace.

import { clsx } from "clsx";
import {
  CHIP_ACTIVE,
  CHIP_BASE,
  CHIP_INACTIVE,
} from "./reportChipStyles";
import type { ReportRange } from "../../../types/report";

interface RangeChoice {
  value: ReportRange;
  /** Terse visible label — the chip row has to fit beside a view toggle. */
  label: string;
  /** Spoken expansion. Prefixed with the visible label so the accessible name
   *  contains it (WCAG 2.5.3 Label in Name) and speech input can still say
   *  "3m", while a screen-reader user hears what the window actually is. */
  spoken: string;
}

const RANGES: readonly RangeChoice[] = [
  { value: "1m", label: "1m", spoken: "last 1 month" },
  { value: "3m", label: "3m", spoken: "last 3 months" },
  { value: "6m", label: "6m", spoken: "last 6 months" },
  { value: "12m", label: "12m", spoken: "last 12 months" },
  { value: "all", label: "Contract to date", spoken: "the whole contract term" },
];

export interface RangeChipsProps {
  value: ReportRange;
  /** Fires on every click, including a re-click of the current selection —
   *  the caller owns whether that is a no-op or a URL rewrite. */
  onChange: (range: ReportRange) => void;
}

export default function RangeChips({ value, onChange }: RangeChipsProps) {
  return (
    <div
      role="group"
      aria-label="Reporting range"
      data-testid="report-range-chips"
      className="flex flex-wrap items-center gap-1.5"
    >
      {RANGES.map((choice) => {
        const active = choice.value === value;
        return (
          <button
            key={choice.value}
            type="button"
            aria-pressed={active}
            aria-label={`${choice.label}, ${choice.spoken}`}
            onClick={() => onChange(choice.value)}
            className={clsx(CHIP_BASE, active ? CHIP_ACTIVE : CHIP_INACTIVE)}
          >
            {choice.label}
          </button>
        );
      })}
    </div>
  );
}
